// Client service for the ReportTemplate API surface.
import axios from 'axios';
import { API_URL } from '../config.js';

function auth() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(fr.result);          // 'data:.../base64,...'
  fr.onerror = reject;
  fr.readAsDataURL(file);
});

export const reportTemplateService = {
  async list() {
    const { data } = await axios.get(`${API_URL}/reports/templates`, auth());
    return data.templates || [];
  },

  async get(id) {
    const { data } = await axios.get(`${API_URL}/reports/templates/${id}`, auth());
    return data.template;
  },

  async createCanvas({ name, description = '', plantId = '', canvas }) {
    const { data } = await axios.post(`${API_URL}/reports/templates`, {
      mode: 'canvas', name, description, plantId, canvas
    }, auth());
    return data.template;
  },

  async createExcel({ name, description = '', plantId = '', file }) {
    const excelBase64 = await fileToBase64(file);
    const { data } = await axios.post(`${API_URL}/reports/templates`, {
      mode: 'excel', name, description, plantId,
      excelBase64,
      excelFileName: file.name
    }, auth());
    return data.template;
  },

  async update(id, patch) {
    const { data } = await axios.patch(`${API_URL}/reports/templates/${id}`, patch, auth());
    return data.template;
  },

  async replaceExcel(id, file) {
    const excelBase64 = await fileToBase64(file);
    const { data } = await axios.put(`${API_URL}/reports/templates/${id}/excel`, {
      excelBase64, excelFileName: file.name
    }, auth());
    return data.template;
  },

  async remove(id) {
    await axios.delete(`${API_URL}/reports/templates/${id}`, auth());
  },

  async cellPreview(id, sheet, opts = {}) {
    const params = {};
    if (sheet) params.sheet = sheet;
    if (opts.padRows) params.padRows = opts.padRows;
    if (opts.padCols) params.padCols = opts.padCols;
    if (opts.maxRows) params.maxRows = opts.maxRows;
    if (opts.maxCols) params.maxCols = opts.maxCols;
    const { data } = await axios.get(`${API_URL}/reports/templates/${id}/cells`, {
      ...auth(), params
    });
    return data.sheets || [];
  },

  async previewBindings(id, reportTime) {
    const { data } = await axios.post(`${API_URL}/reports/templates/${id}/preview`, {
      reportTime: reportTime ? new Date(reportTime).toISOString() : undefined
    }, auth());
    return data;
  },

  async generate(id, { reportTime, save = false } = {}) {
    if (save) {
      const { data } = await axios.post(`${API_URL}/reports/templates/${id}/generate`, {
        reportTime, save: true
      }, auth());
      return data;
    }
    const res = await axios.post(`${API_URL}/reports/templates/${id}/generate`, {
      reportTime, save: false
    }, { ...auth(), responseType: 'blob' });
    return res.data;          // Blob
  },

  async schedule(id, { name, schedule, appendMode = false }) {
    const { data } = await axios.post(`${API_URL}/reports/scheduled`, {
      name,
      templateId: id,
      schedule,
      appendMode,
      format: 'excel'
    }, auth());
    return data.report;
  }
};

export default reportTemplateService;
